/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the certificates module.
────────────────────────────────────────────────────────────────*/


import api from '../../lib/api';
import { Certificate, CertificateListItem, CertificateFormData, CertificateInfo } from './types';

const BASE_URL = '/api/maintenance/certificates';

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

/**
 * Fetch all certificates
 */
export const fetchCertificates = async (search?: string): Promise<CertificateListItem[]> => {
    const params = search ? { search } : {};
    const response = await api.get<ApiResponse<CertificateListItem[]>>(BASE_URL, { params });
    return response.data.data || [];
};

/**
 * Fetch a single certificate by UUID
 */
export const fetchCertificate = async (uuid: string): Promise<Certificate> => {
    const response = await api.get<ApiResponse<Certificate>>(`${BASE_URL}/${uuid}`);
    if (!response.data.data) {
        throw new Error('Certificate not found');
    }
    return response.data.data;
};

/**
 * Create a new certificate
 */
export const createCertificate = async (data: Partial<CertificateFormData>): Promise<Certificate> => {
    const response = await api.post<ApiResponse<Certificate>>(BASE_URL, data);
    if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to create certificate');
    }
    return response.data.data!;
};

/**
 * Update an existing certificate
 */
export const updateCertificate = async (id: number, data: Partial<CertificateFormData>): Promise<Certificate> => {
    const response = await api.put<ApiResponse<Certificate>>(`${BASE_URL}/${id}`, data);
    if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update certificate');
    }
    return response.data.data!;
};

/**
 * Delete a certificate
 */
export const deleteCertificate = async (id: number): Promise<void> => {
    const response = await api.delete<ApiResponse<void>>(`${BASE_URL}/${id}`);
    if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete certificate');
    }
};

/**
 * Parse/validate a certificate without saving
 */
export const parseCertificate = async (certificate: string, privateKey?: string): Promise<CertificateInfo> => {
    const response = await api.post<ApiResponse<CertificateInfo>>(`${BASE_URL}/parse`, {
        certificate,
        privateKey,
    });
    if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to parse certificate');
    }
    return response.data.data!;
};

/**
 * Generate a preview of self-signed certificate
 */
export const previewSelfSigned = async (data: {
    commonName: string;
    subjectAltNames?: string[];
    keySize?: number;
    validityDays?: number;
}): Promise<{ certificate: string; certificateInfo: CertificateInfo }> => {
    const response = await api.post<ApiResponse<{ certificate: string; certificateInfo: CertificateInfo }>>(
        `${BASE_URL}/preview-self-signed`,
        data
    );
    if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to generate certificate preview');
    }
    return response.data.data!;
};

/**
 * Get download URL for a certificate file
 */
export const getDownloadUrl = (uuid: string, fileType: 'cert' | 'key' | 'chain' | 'fullchain'): string => {
    return `${api.defaults.baseURL}${BASE_URL}/${uuid}/download/${fileType}`;
};

/**
 * Confirm manual DNS-01 challenge — call after adding the TXT record to DNS
 */
export const confirmDnsChallenge = async (id: number): Promise<void> => {
    const response = await api.post<ApiResponse<void>>(`${BASE_URL}/${id}/confirm-dns`, {}, {
        timeout: 300000 // 5 minutes - certbot DNS verification can be very slow
    });
    if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to confirm DNS challenge');
    }
};

